import { appState } from './state';
import { listExpenses, type Expense } from '@/storage/repo';
import { CATEGORY_BADGE, FALLBACK_BADGE, formatZAR, MASKED } from './format';
import { icon, refreshIcons } from './icons';
import { metaSet } from '@/storage/repo';

let currentList: Expense[] = [];

export function recalcAndRender() {
  const root = document.getElementById('mainContent');
  if (!root) return;
  void renderMain(root);
}

function getShow(s = appState.get()) { return !s.amountsHidden; }

export async function renderMain(root: HTMLElement) {
  const s = appState.get();
  const expenses = await listExpenses(s.currentMonth);
  currentList = expenses;

  const totalBudgeted = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalPaid     = expenses.filter(e => e.paid).reduce((sum, e) => sum + e.amount, 0);
  const unpaid        = totalBudgeted - totalPaid;
  const pct           = totalBudgeted > 0 ? (totalPaid / totalBudgeted) * 100 : 0;
  const showReal = getShow();

  root.innerHTML = `
    <main class="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">

      <!-- Income -->
      <section class="bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-sm" style="backdrop-filter: blur(8px)">
        <label for="incomeInput" class="block text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Monthly Income Baseline</label>
        <div class="relative max-w-xs">
          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">R</span>
          <input id="incomeInput" type="number" step="0.01" min="0" value="${s.income.toFixed(2)}" class="w-full pl-8 pr-3 py-2 text-base font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
      </section>

      <!-- KPIs -->
      <section class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        ${kpi('trending-up', 'Total Income', showReal ? formatZAR(s.income) : MASKED, 'text-indigo-600 dark:text-indigo-400', 'income')}
        ${kpi('receipt', 'Budgeted', showReal ? formatZAR(totalBudgeted) : MASKED, 'text-slate-700 dark:text-slate-200', 'budgeted')}
        ${kpi('check-circle-2', 'Total Paid', showReal ? formatZAR(totalPaid) : MASKED, 'text-emerald-600 dark:text-emerald-400', 'paid')}
        ${kpi('circle-dollar-sign', 'Unpaid Balance', showReal ? formatZAR(unpaid) : MASKED, 'text-rose-600 dark:text-rose-400', 'unpaid')}
      </section>

      <!-- Progress -->
      <section class="bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-sm" style="backdrop-filter: blur(8px)">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            ${icon('gauge', 'w-4 h-4 text-slate-500 dark:text-slate-400')}
            <span class="text-sm font-medium text-slate-600 dark:text-slate-300">Spending progress</span>
          </div>
          <div class="flex items-baseline gap-1">
            <span id="progressPct" class="text-sm font-semibold text-slate-700 dark:text-slate-200">${pct.toFixed(1)}%</span>
            <span class="text-xs text-slate-500 dark:text-slate-400">paid of budgeted</span>
          </div>
        </div>
        <div class="h-3 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
          <div id="progressFill" class="progress-fill h-full rounded-full bg-gradient-to-r from-emerald-500 to-indigo-500" style="width:${Math.min(100, pct).toFixed(2)}%"></div>
        </div>
      </section>

      <!-- Filters + add -->
      <section class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div class="inline-flex p-1 rounded-xl bg-slate-200/60 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800 self-start">
          ${filterPill('all',    'All Items',    s.filter)}
          ${filterPill('paid',   'Paid Only',    s.filter)}
          ${filterPill('unpaid', 'Unpaid Only',  s.filter)}
        </div>
        <div class="flex items-center gap-2">
          <a href="#/suggest" class="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-violet-200 text-violet-700 dark:border-violet-700/50 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 text-sm font-semibold transition">
            ${icon('sparkles', 'w-4 h-4')} Suggestions
          </a>
          <button id="addExpenseBtn" class="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition">
            ${icon('plus', 'w-4 h-4')} Add New Expense
          </button>
        </div>
      </section>

      <!-- List -->
      <section id="expenseList" class="space-y-4"></section>

      <div id="emptyState" class="hidden text-center py-12">
        <div class="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 mb-3">
          ${icon('package-open', 'w-7 h-7 text-slate-400')}
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">No expenses match the current filter.</p>
      </div>

      <footer class="text-center text-xs text-slate-400 dark:text-slate-500 pt-6 pb-10">
        Data is stored locally in your browser via SQLite + OPFS.
        Clearing site data will reset your tracker.
      </footer>
    </main>
  `;
  refreshIcons();
  renderList(expenses, s.filter);
  wireHome();
}

function kpi(iconName: string, label: string, value: string, valueClass: string, key?: string) {
  return `
    <div class="bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-sm" style="backdrop-filter: blur(8px)">
      <div class="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
        ${icon(iconName, 'w-3.5 h-3.5')} ${label}
      </div>
      <div class="mt-2 text-xl sm:text-2xl font-bold ${valueClass}"${key ? ` data-kpi="${key}"` : ''}>${value}</div>
    </div>
  `;
}

function filterPill(key: 'all' | 'paid' | 'unpaid', label: string, current: string) {
  const active = key === current;
  const cls = active
    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-white shadow-sm'
    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white';
  return `<button data-filter="${key}" class="filter-pill px-3 py-1.5 text-sm font-medium rounded-lg transition ${cls}">${label}</button>`;
}

function renderList(expenses: Expense[], filter: 'all' | 'paid' | 'unpaid') {
  const container = document.getElementById('expenseList')!;
  const empty = document.getElementById('emptyState')!;

  const sorted = expenses.slice().sort((a, b) => {
    if (a.paid !== b.paid) return a.paid ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
  const filtered = sorted.filter(e =>
    filter === 'paid' ? e.paid :
    filter === 'unpaid' ? !e.paid : true);

  if (filtered.length === 0) {
    container.innerHTML = '';
    empty.classList.remove('hidden');
    return;
  }
  empty.classList.add('hidden');

  // Group by category
  const groups = new Map<string, Expense[]>();
  for (const e of filtered) {
    if (!groups.has(e.category)) groups.set(e.category, []);
    groups.get(e.category)!.push(e);
  }

  const orderedCats = [...groups.keys()].sort();

  container.innerHTML = orderedCats.map(cat => {
    const items = groups.get(cat)!;
    const subtotal = items.reduce((s, e) => s + e.amount, 0);
    const paidSub  = items.filter(e => e.paid).reduce((s, e) => s + e.amount, 0);
    const showReal = getShow();
    const badge = CATEGORY_BADGE[cat] || FALLBACK_BADGE;

    return `
      <div class="bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden" style="backdrop-filter: blur(8px)">
        <div class="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40">
          <div class="flex items-center gap-2 min-w-0">
            <span class="px-2.5 py-1 text-xs font-semibold rounded-full ${badge}">${escapeHtml(cat)}</span>
            <span class="text-xs text-slate-500 dark:text-slate-400">${items.length} item${items.length === 1 ? '' : 's'}</span>
          </div>
          <div class="text-xs sm:text-sm tabular-nums">
            <span class="text-emerald-600 dark:text-emerald-400 font-semibold">${showReal ? formatZAR(paidSub) : MASKED}</span>
            <span class="text-slate-400"> / </span>
            <span class="font-semibold text-slate-700 dark:text-slate-200">${showReal ? formatZAR(subtotal) : MASKED}</span>
          </div>
        </div>
        <ul class="divide-y divide-slate-100 dark:divide-slate-800">
          ${items.map(rowHtml).join('')}
        </ul>
      </div>
    `;
  }).join('');
  refreshIcons();
}

function rowHtml(e: Expense) {
  const nameClass = e.paid ? 'paid-name' : '';
  const dateHtml = e.paidAt
    ? `<span class="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">${icon('calendar-check', 'w-3 h-3')} ${formatZAR(0).replace(/[\d.,\s]/g, '')} ${new Date(e.paidAt).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}</span>`
    : `<span class="inline-flex items-center gap-1 text-xs text-slate-400">${icon('calendar-x', 'w-3 h-3')} —</span>`;
  const badge = CATEGORY_BADGE[e.category] || FALLBACK_BADGE;
  const showReal = getShow();
  return `
    <li class="row-enter px-4 sm:px-5 py-3 flex items-start sm:items-center gap-3 hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition">
      <label class="pt-0.5 sm:pt-0 cursor-pointer">
        <input type="checkbox" class="nice-check js-toggle" data-id="${e.id}" ${e.paid ? 'checked' : ''} aria-label="Mark ${escapeHtml(e.name)} as paid" />
      </label>
      <div class="flex-1 min-w-0">
        <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span class="font-medium ${nameClass} truncate">${escapeHtml(e.name)}</span>
          <span class="px-2 py-0.5 text-[10px] font-semibold rounded-full ${badge}">${escapeHtml(e.category)}</span>
          ${e.source && e.source !== 'manual' ? `<span class="text-[10px] text-slate-400">· ${escapeHtml(e.source)}</span>` : ''}
        </div>
        ${e.notes ? `<p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">${escapeHtml(e.notes)}</p>` : ''}
        <div class="mt-1">${dateHtml}</div>
      </div>
      <div class="text-right shrink-0 flex items-center gap-3">
        <div class="text-sm sm:text-base font-semibold tabular-nums ${e.paid ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-800 dark:text-slate-100'}">
          ${showReal ? formatZAR(e.amount) : MASKED}
        </div>
        <button data-id="${e.id}" class="js-delete inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition" title="Delete expense" aria-label="Delete ${escapeHtml(e.name)}">
          ${icon('trash', 'w-4 h-4')}
        </button>
      </div>
    </li>
  `;
}

function wireHome() {
  const incomeEl = document.getElementById('incomeInput') as HTMLInputElement | null;
  let incomeTimer: any;
  incomeEl?.addEventListener('input', () => {
    clearTimeout(incomeTimer);
    incomeTimer = setTimeout(async () => {
      const v = Math.max(0, Number(incomeEl.value) || 0);
      appState.set(s => ({ ...s, income: v }));
      await metaSet('income', String(v));
      // Update only KPI value, not whole tree
      const kpi = document.querySelector('[data-kpi="income"]');
      if (kpi) kpi.textContent = appState.get().amountsHidden ? MASKED : formatZAR(v);
    }, 200);
  });

  document.querySelectorAll<HTMLButtonElement>('.filter-pill').forEach(btn => {
    btn.addEventListener('click', () => {
      const f = btn.dataset.filter as 'all' | 'paid' | 'unpaid';
      appState.set(s => ({ ...s, filter: f }));
      metaSet('filter', f);
      renderList(currentList, f);
      // Update active styling
      document.querySelectorAll('.filter-pill').forEach(b => {
        const active = (b as HTMLElement).dataset.filter === f;
        b.className = b.className
          .replace(/bg-white dark:bg-slate-700 text-indigo-600 dark:text-white shadow-sm|text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white/g, '')
          .trim();
        b.className += ' ' + (active
          ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-white shadow-sm'
          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white');
      });
    });
  });

  document.getElementById('addExpenseBtn')?.addEventListener('click', () => {
    openAddModal();
  });

  document.getElementById('expenseList')?.addEventListener('click', async (e) => {
    const del = (e.target as HTMLElement).closest('.js-delete') as HTMLElement | null;
    if (del) {
      const id = del.dataset.id!;
      if (!confirm('Delete this expense?')) return;
      const { deleteExpense } = await import('@/storage/repo');
      await deleteExpense(id);
      recalcAndRender();
    }
  });
  document.getElementById('expenseList')?.addEventListener('change', async (e) => {
    const cb = (e.target as HTMLElement).closest('.js-toggle') as HTMLInputElement | null;
    if (!cb) return;
    const { togglePaid } = await import('@/storage/repo');
    await togglePaid(cb.dataset.id!);
    recalcAndRender();
  });
}

function escapeHtml(s: string) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]!));
}

let modalOpen = false;
export async function openAddModal() {
  if (modalOpen) return;
  modalOpen = true;
  const { CATEGORIES } = await import('./format');
  const wrap = document.createElement('div');
  wrap.id = 'modal';
  wrap.className = 'fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4';
  wrap.innerHTML = `
    <div id="modalBackdrop" class="absolute inset-0 bg-slate-900/60" style="backdrop-filter: blur(4px)"></div>
    <div class="relative w-full sm:max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-t-2xl sm:rounded-2xl shadow-xl">
      <div class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
        <div class="flex items-center gap-2">
          ${icon('plus-square', 'w-4 h-4 text-indigo-500')}
          <h2 class="font-semibold">Add New Expense</h2>
        </div>
        <button id="modalClose" class="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">${icon('x', 'w-4 h-4')}</button>
      </div>
      <form id="addForm" class="p-4 space-y-3">
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Name *</label>
          <input name="name" required type="text" placeholder="e.g. Internet" class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Category *</label>
          <select name="category" required class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500">
            ${CATEGORIES.map(c => `<option value="${c}">${c}</option>`).join('')}
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Amount (ZAR) *</label>
          <div class="relative">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">R</span>
            <input name="amount" required type="number" step="0.01" min="0" placeholder="0.00" class="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Notes</label>
          <input name="notes" type="text" placeholder="e.g. Debit order on the 5th" class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div class="flex items-center justify-end gap-2 pt-2">
          <button type="button" id="modalCancel" class="px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
          <button type="submit" class="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white">Add Expense</button>
        </div>
      </form>
    </div>
  `;
  document.body.appendChild(wrap);
  refreshIcons();

  const close = () => {
    wrap.remove();
    modalOpen = false;
  };
  wrap.querySelector('#modalClose')?.addEventListener('click', close);
  wrap.querySelector('#modalCancel')?.addEventListener('click', close);
  wrap.querySelector('#modalBackdrop')?.addEventListener('click', close);
  wrap.querySelector('#addForm')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const f = e.currentTarget as HTMLFormElement;
    const fd = new FormData(f);
    const payload = {
      ym: appState.get().currentMonth,
      name: String(fd.get('name') || '').trim(),
      category: String(fd.get('category') || '') as any,
      amount: Math.round(Number(fd.get('amount')) * 100) / 100,
      notes: String(fd.get('notes') || '').trim(),
    };
    if (!payload.name || !payload.category || !payload.amount) return;
    const { addExpense } = await import('@/storage/repo');
    await addExpense(payload);
    close();
    recalcAndRender();
  });
}
